// The bundle's own trained poses — the table every rig constraint is measured from.
// Notes: ~/comments/aos-threejs-poc/poseTable_loadPoseTable_width_must_match.md

import { parseNpyFloat32 } from "../../lib/npyFloat32.js";
import { resolveRigPresetsFile } from "../rig/rigBoardSource.js";
import type { PoseTable } from "./rigConstraints.ts";
import type { OgsLogger } from "./ogsLog.ts";

export interface LoadPoseTableInput {
  /** The raw scene.json — it names the file. */
  scene: Record<string, unknown> | null;
  fetchBytes(name: string): Promise<Uint8Array | ArrayBuffer>;
  /** The control-space width, from rig_names.json. 0 when it is unknown. */
  width: number;
  log: OgsLogger;
}

/**
 * Null when the bundle ships no readable table — every constraint then stands down and the
 * deform anchors at the ZERO rig, which is only correct for a space centred on zero.
 *
 * Never throws: a missing table is a degraded character, not a failed boot.
 */
export async function loadPoseTable(input: LoadPoseTableInput): Promise<PoseTable | null> {
  const file = resolveRigPresetsFile(input.scene);
  if (!file) return null;
  try {
    const { data, cols } = parseNpyFloat32(await input.fetchBytes(file));
    if (!cols || data.length < cols) return null;
    // WIDTH MUST MATCH the control space. A narrower table is zero-padded downstream,
    // re-introducing the "zero is an extreme corner" bug this table exists to fix — while
    // the ARKit path would ignore it outright, so the geometry anchor and the live rig
    // would disagree. Refuse it: both then fall back to the SAME behaviour.
    if (input.width && cols !== input.width) {
      input.log.warn(`${file} is ${cols} controls but the rig is ${input.width} — ignoring it and `
        + "anchoring at the ZERO rig, which is only correct if this control space rests at zero");
      return null;
    }
    return { rest: data.slice(0, cols), presets: data, dim: cols };
  } catch (error) {
    input.log.warn(`rest rig unavailable (${file}) — anchoring the deform at the ZERO rig, `
      + `which is only correct if this control space rests at zero: ${error}`);
    return null;
  }
}
